import { type Grade, type GradeCategory } from "@core/grades/types";
import { trans, plainTrans } from "@mongez/localization";
import { useMemo } from "react";
import { GradeCategoriesSection } from "../../grades/components/GradeCategoriesSection";
import { BookIcon } from "lucide-react";

type GradesCategoriesSectionProps = {
  gradesCategories: GradeCategory[];
  className?: string;
  containerClassName?: string;
  showBorder?: boolean;
};

export function GradesCategoriesSection({
  gradesCategories,
  className,
  containerClassName = "rounded-2xl bg-white dark:bg-gray-800 p-8 transition-colors duration-300",
  showBorder = true,
}: GradesCategoriesSectionProps) {
  // Group categories by grade
  const categoriesByGrade = useMemo(() => {
    const grouped: {
      [gradeId: number]: { grade: Grade; categories: GradeCategory[] };
    } = {};

    gradesCategories.forEach((category) => {
      if (category.grade) {
        const gradeId = category.grade.id;
        if (!grouped[gradeId]) {
          grouped[gradeId] = {
            grade: category.grade,
            categories: [],
          };
        }
        grouped[gradeId].categories.push(category);
      }
    });

    return Object.values(grouped);
  }, [gradesCategories]);

  if (!categoriesByGrade.length) return null;

  return (
    <div className={className}>
      {categoriesByGrade.map(({ grade, categories }, index) => (
        <div key={grade.id} className={containerClassName}>
          {showBorder && index > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-8" />
          )}
          <GradeCategoriesSection
            categories={categories}
            icon={<BookIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
            title={plainTrans("grades.gradeCategoriesList", {
              grade: grade.name,
            })}
          />
        </div>
      ))}
    </div>
  );
}
